import React, { useState, useEffect } from "react";
import { Icon } from "../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";

/* ============================================================
   Talention — Departamentos, Niveles, Puestos
   ============================================================ */

const DEPT_PALETTE = ["#0a84ff", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2", "#9f1239"];
function deptColor(i) { return DEPT_PALETTE[i % DEPT_PALETTE.length]; }

/* ---- Departments ---- */
function Departments() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null); // dept | "new" | null
  const [query, setQuery] = useState("");

  const filtered = state.departments.filter(d =>
    !query || d.name.toLowerCase().includes(query.toLowerCase())
  );
  const deptColorMap = Object.fromEntries(state.departments.map((d, i) => [d.id, deptColor(i)]));

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Departamentos"
        sub="Estructura organizativa por áreas funcionales. Asocia los puestos a un departamento."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>Nuevo departamento</Button>}
      />

      <div className="grid grid-3">
        <KPIMini label="Total departamentos" value={state.departments.length} icon="users"    color="#007aff" />
        <KPIMini label="Puestos asignados"   value={state.positions.length} icon="briefcase" color="#34c759" />
        <KPIMini label="Sin puestos"         value={state.departments.filter(d => !state.positions.some(p => p.departmentId === d.id)).length} icon="alert" color="#ff9f0a" />
      </div>

      <Card className="flush">
        <div style={{
          padding: "13px 20px", background: "var(--bg-soft)", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--ink-900)", color: "white", display: "grid", placeItems: "center" }}>
              <Icon name="users" size={14} />
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em" }}>Árbol de departamentos</span>
          </div>
          <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>{state.departments.length} departamentos</span>
        </div>
        <div style={{ padding: "14px 20px", display: "flex", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flex: 1, maxWidth: 360,
            padding: "7px 12px", borderRadius: 999, background: "var(--bg-soft)", border: "1px solid var(--border)",
          }}>
            <Icon name="search" size={14} className="muted" />
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Buscar departamento..." style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, flex: 1 }} />
          </div>
          <Badge>{filtered.length} de {state.departments.length}</Badge>
        </div>
        <table className="t-table">
          <thead><tr>
            <th style={{ width: 60 }}>ID</th>
            <th>Departamento</th>
            <th>Descripción</th>
            <th>Puestos</th>
            <th>Fecha Alta</th>
            <th style={{ width: 110 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {filtered.map(d => {
              const count = state.positions.filter(p => p.departmentId === d.id).length;
              return (
                <tr key={d.id}>
                  <td><span className="row-id">#{d.id}</span></td>
                  <td>
                    <div className="row" style={{ gap: 10 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: deptColorMap[d.id] + "18",
                        color: deptColorMap[d.id],
                        display: "grid", placeItems: "center",
                        fontWeight: 700, fontSize: 12,
                        boxShadow: `0 2px 6px ${deptColorMap[d.id]}25`,
                      }}>{d.name[0]}</div>
                      <b>{d.name}</b>
                    </div>
                  </td>
                  <td className="muted">{d.description}</td>
                  <td><Badge tone={count > 0 ? "good" : ""}>{count}</Badge></td>
                  <td className="muted tiny">{d.createdAt}</td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <button className="icon-btn" onClick={() => setEditing(d)} title="Editar"><Icon name="edit" size={14} /></button>
                      <button className="icon-btn" onClick={() => {
                        if (confirm(`¿Eliminar el departamento "${d.name}"?`)) dispatch({ type: "DELETE_DEPT", id: d.id });
                      }} title="Eliminar"><Icon name="trash" size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="empty">No se encontraron departamentos</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <DeptModal
        open={editing !== null}
        dept={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_DEPT", dept: data });
          else dispatch({ type: "UPDATE_DEPT", id: editing.id, patch: data });
          setEditing(null);
        }}
      />
    </div>
  );
}

function DeptModal({ open, dept, onClose, onSave }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  useEffect(() => {
    setName(dept?.name || "");
    setDescription(dept?.description || "");
  }, [open, dept]);
  return (
    <Modal open={open} onClose={onClose} title={dept ? "Editar departamento" : "Nuevo departamento"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => name.trim() && onSave({ name, description })}>
            {dept ? "Guardar cambios" : "Crear departamento"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field label="Nombre del departamento">
          <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Recursos Humanos" autoFocus />
        </Field>
        <Field label="Descripción" hint="Una breve descripción del propósito del departamento">
          <textarea className="textarea" value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Departamento encargado de..." />
        </Field>
      </div>
    </Modal>
  );
}

function KPIMini({ label, value, icon, color }) {
  const c = color || "var(--accent)";
  const tint = color ? color + "18" : "var(--accent-light)";
  return (
    <div className="kpi-card kpi-card-mini" style={{ "--kpi-color": c }}>
      <div className="kpi-head">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon" style={{ background: tint, color: c }}>
          <Icon name={icon} size={14} />
        </span>
      </div>
      <div className="kpi-num">{value}</div>
    </div>
  );
}

/* ---- Levels ---- */

const TIER_PALETTE = [
  "#1d1d1f",  // L1 — executive
  "#1d4ed8",  // L2 — blue
  "#7c3aed",  // L3 — violet
  "#0891b2",  // L4 — cyan
  "#059669",  // L5 — emerald
  "#d97706",  // L6 — amber
  "#dc2626",  // L7 — red
];

function tierColor(i) {
  return TIER_PALETTE[i % TIER_PALETTE.length];
}

function Levels() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null);
  const sorted = [...state.levels].sort((a, b) => a.order - b.order);
  const total = sorted.length;

  const positionCounts = Object.fromEntries(
    sorted.map(lvl => [lvl.id, state.positions.filter(p => p.levelId === lvl.id).length])
  );
  const totalAssigned = Object.values(positionCounts).reduce((s, n) => s + n, 0);
  const unassigned = state.positions.length - totalAssigned;

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Niveles Jerárquicos"
        sub="Pirámide organizativa que clasifica los puestos por nivel de responsabilidad."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>Nuevo nivel</Button>}
      />

      <div className="grid grid-3">
        <KPIMini label="Niveles definidos"  value={total}         icon="tree"      color="#7c3aed" />
        <KPIMini label="Puestos asignados"  value={totalAssigned} icon="briefcase" color="#0891b2" />
        <KPIMini label="Sin nivel asignado" value={unassigned}    icon="alert"     color="#d97706" />
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {/* Card header */}
        <div style={{
          padding: "13px 20px",
          borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "var(--bg-soft)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Mini pyramid graphic */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2.5 }}>
              {[14, 10, 6].map((w, j) => (
                <div key={j} style={{
                  height: 3, width: w, borderRadius: 2,
                  background: j === 0 ? "var(--ink-800)" : j === 1 ? "var(--ink-400)" : "var(--ink-200)",
                }} />
              ))}
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em", color: "var(--ink-900)" }}>
              Pirámide jerárquica
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>
              {state.positions.length} puestos totales
            </span>
            <span style={{
              fontSize: 11, padding: "2px 9px", borderRadius: 999,
              background: "var(--ink-100)", color: "var(--ink-600)", fontWeight: 600,
            }}>{total} {total === 1 ? "nivel" : "niveles"}</span>
          </div>
        </div>

        {/* Level rows */}
        <div>
          {sorted.map((lvl, i) => {
            const count = positionCounts[lvl.id] || 0;
            const barWidth = ((total - i) / total) * 100;
            const color = tierColor(i);
            const isLast = i === sorted.length - 1;

            return (
              <div
                key={lvl.id}
                className="level-row"
                style={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: "52px 1fr auto",
                  alignItems: "center",
                  gap: 18,
                  padding: "18px 20px 18px 24px",
                  borderBottom: isLast ? "none" : "1px solid var(--border)",
                  overflow: "hidden",
                }}
              >
                {/* Pyramid background tint */}
                <div style={{
                  position: "absolute",
                  left: 3, top: 0, bottom: 0,
                  width: `calc(${barWidth}% - 3px)`,
                  background: color + "09",
                  pointerEvents: "none",
                }} />

                {/* Left tier border */}
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: 3, background: color,
                }} />

                {/* Order badge */}
                <div style={{
                  width: 44, height: 44,
                  borderRadius: 12,
                  background: color,
                  color: "white",
                  display: "grid", placeItems: "center",
                  fontWeight: 800, fontSize: 18,
                  letterSpacing: "-0.025em",
                  flexShrink: 0,
                  boxShadow: `0 4px 12px ${color}45`,
                  position: "relative", zIndex: 1,
                }}>
                  {lvl.order}
                </div>

                {/* Content */}
                <div style={{ minWidth: 0, position: "relative", zIndex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 3, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 680, fontSize: 15, letterSpacing: "-0.018em", color: "var(--ink-900)" }}>
                      {lvl.name}
                    </span>
                    <span style={{
                      fontSize: 11, padding: "2px 8px", borderRadius: 999,
                      background: count > 0 ? color + "18" : "var(--ink-100)",
                      color: count > 0 ? color : "var(--ink-400)",
                      fontWeight: 650,
                      border: `1px solid ${count > 0 ? color + "35" : "transparent"}`,
                      whiteSpace: "nowrap",
                    }}>
                      {count} {count === 1 ? "puesto" : "puestos"}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--ink-500)", marginBottom: 10, lineHeight: 1.45 }}>
                    {lvl.description}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <div style={{
                      flex: 1, maxWidth: 220, height: 5,
                      background: "var(--ink-100)", borderRadius: 999, overflow: "hidden",
                    }}>
                      <div style={{
                        height: "100%", width: barWidth + "%",
                        background: color, borderRadius: 999,
                        transition: "width 600ms cubic-bezier(0.32, 0.72, 0, 1)",
                      }} />
                    </div>
                    <span style={{
                      fontSize: 10.5, color: "var(--ink-400)",
                      fontFamily: "var(--font-mono)",
                    }}>
                      L{lvl.order}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 6, alignItems: "center", position: "relative", zIndex: 1 }}>
                  <button className="icon-btn" onClick={() => setEditing(lvl)} title="Editar">
                    <Icon name="edit" size={14} />
                  </button>
                  <button className="icon-btn" onClick={() => {
                    if (confirm(`¿Eliminar el nivel "${lvl.name}"?`)) dispatch({ type: "DELETE_LEVEL", id: lvl.id });
                  }} title="Eliminar">
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </div>
            );
          })}

          {sorted.length === 0 && (
            <div className="empty">No hay niveles jerárquicos definidos</div>
          )}
        </div>
      </Card>

      <LevelModal
        open={editing !== null}
        lvl={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        defaultOrder={Math.max(0, ...state.levels.map(l => l.order)) + 1}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_LEVEL", lvl: data });
          else dispatch({ type: "UPDATE_LEVEL", id: editing.id, patch: data });
          setEditing(null);
        }}
      />
    </div>
  );
}

function LevelModal({ open, lvl, onClose, onSave, defaultOrder }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState(1);
  useEffect(() => {
    setName(lvl?.name || "");
    setDescription(lvl?.description || "");
    setOrder(lvl?.order || defaultOrder);
  }, [open, lvl]);
  return (
    <Modal open={open} onClose={onClose} title={lvl ? "Editar nivel" : "Nuevo nivel jerárquico"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => name.trim() && onSave({ name, description, order })}>
            {lvl ? "Guardar" : "Crear nivel"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field label="Nombre"><input className="input" value={name} onChange={e => setName(e.target.value)} autoFocus /></Field>
        <Field label="Descripción"><textarea className="textarea" rows={3} value={description} onChange={e => setDescription(e.target.value)} /></Field>
        <Field label="Orden jerárquico" hint="1 = nivel más alto"><input type="number" className="input" value={order} onChange={e => setOrder(parseInt(e.target.value) || 1)} /></Field>
      </div>
    </Modal>
  );
}

/* ---- Positions ---- */
function Positions() {
  const { state, dispatch } = useStore();
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");

  const filtered = state.positions.filter(p => {
    if (deptFilter !== "all" && p.departmentId !== parseInt(deptFilter)) return false;
    if (query && !p.name.toLowerCase().includes(query.toLowerCase()) && !p.code.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Puestos"
        sub="Inventario de puestos de trabajo registrados en la organización."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>Nuevo puesto</Button>}
      />

      <Card className="flush">
        <div style={{
          padding: "13px 20px", background: "var(--bg-soft)", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--ink-900)", color: "white", display: "grid", placeItems: "center" }}>
              <Icon name="briefcase" size={14} />
            </div>
            <span style={{ fontWeight: 650, fontSize: 13.5, letterSpacing: "-0.015em" }}>Inventario de puestos</span>
          </div>
          <span style={{ fontSize: 11.5, color: "var(--ink-400)" }}>{state.positions.length} puestos totales</span>
        </div>
        <div style={{ padding: "14px 20px", display: "flex", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border)" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flex: 1, maxWidth: 320,
            padding: "7px 12px", borderRadius: 999, background: "var(--bg-soft)", border: "1px solid var(--border)",
          }}>
            <Icon name="search" size={14} className="muted" />
            <input value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Buscar puesto o código..." style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, flex: 1 }} />
          </div>
          <select className="select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ width: "auto" }}>
            <option value="all">Todos los departamentos</option>
            {state.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Badge>{filtered.length} puestos</Badge>
        </div>
        <table className="t-table">
          <thead><tr>
            <th style={{ width: 90 }}>Código</th>
            <th>Puesto</th>
            <th>Departamento</th>
            <th>Nivel jerárquico</th>
            <th>Estado valoración</th>
            <th style={{ width: 110 }}>Acciones</th>
          </tr></thead>
          <tbody>
            {filtered.map(p => {
              const dept = state.departments.find(d => d.id === p.departmentId);
              const lvl = state.levels.find(l => l.id === p.levelId);
              return (
                <tr key={p.id}>
                  <td><span className="row-id mono">{p.code}</span></td>
                  <td><b>{p.name}</b></td>
                  <td>{dept?.name || "—"}</td>
                  <td>
                    {lvl ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "2px 9px", borderRadius: 999, fontSize: 11.5,
                        background: tierColor(lvl.order - 1) + "18",
                        color: tierColor(lvl.order - 1),
                        fontWeight: 650,
                        border: `1px solid ${tierColor(lvl.order - 1)}30`,
                        whiteSpace: "nowrap",
                      }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: tierColor(lvl.order - 1), flexShrink: 0 }} />
                        {lvl.name}
                      </span>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td>
                    <ValuationStatus position={p} state={state} />
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <button className="icon-btn" title="Valorar" onClick={() => window.GO("/valuation/" + p.id)}>
                        <Icon name="check-list" size={14} />
                      </button>
                      <button className="icon-btn" onClick={() => setEditing(p)}><Icon name="edit" size={14} /></button>
                      <button className="icon-btn" onClick={() => {
                        if (confirm(`¿Eliminar el puesto "${p.name}"?`)) dispatch({ type: "DELETE_POSITION", id: p.id });
                      }}><Icon name="trash" size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="6" className="empty">No se encontraron puestos</td></tr>}
          </tbody>
        </table>
      </Card>

      <PositionModal
        open={editing !== null}
        pos={editing === "new" ? null : editing}
        depts={state.departments}
        levels={state.levels}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (editing === "new") dispatch({ type: "ADD_POSITION", pos: data });
          else dispatch({ type: "UPDATE_POSITION", id: editing.id, patch: data });
          setEditing(null);
        }}
      />
    </div>
  );
}

function PositionModal({ open, pos, depts, levels, onClose, onSave }) {
  const [name, setName] = useState("");
  const [departmentId, setDepartmentId] = useState(0);
  const [levelId, setLevelId] = useState(0);
  useEffect(() => {
    setName(pos?.name || "");
    setDepartmentId(pos?.departmentId || depts[0]?.id || 0);
    setLevelId(pos?.levelId || levels[0]?.id || 0);
  }, [open, pos]);
  return (
    <Modal open={open} onClose={onClose} title={pos ? "Editar puesto" : "Nuevo puesto"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => name.trim() && onSave({ name, departmentId: parseInt(departmentId), levelId: parseInt(levelId) })}>
            {pos ? "Guardar" : "Crear puesto"}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field label="Denominación del puesto"><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Comercial Senior" autoFocus /></Field>
        <div className="grid grid-2">
          <Field label="Departamento">
            <select className="select" value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Nivel jerárquico">
            <select className="select" value={levelId} onChange={e => setLevelId(e.target.value)}>
              {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/* Shared status component for valuations */
function ValuationStatus({ position, state }) {
  const tone = position.status === "FINALIZADA" ? "good"
    : position.status === "EN PROGRESO" ? "info"
    : "neutral";
  const label = position.status === "FINALIZADA" ? "Finalizada"
    : position.status === "EN PROGRESO" ? "En progreso"
    : "Pendiente";
  const dotColor = tone === "good" ? "#34c759" : tone === "info" ? "#0a84ff" : "#9aa1ad";

  let detail = null;
  if (position.status === "FINALIZADA") {
    detail = (
      <span style={{ fontSize: 12, color: "var(--ink-500)" }}>
        <span className="mono semi" style={{ color: "var(--ink-900)" }}>{position.points}</span> pts
      </span>
    );
  } else if (position.status === "EN PROGRESO") {
    const v = state.valuations[position.id];
    const counts = v ? TALENTION_HELPERS.countCompletedItems(v, state.factors) : null;
    if (counts) {
      const pct = counts.total ? Math.round(counts.done / counts.total * 100) : 0;
      detail = (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 38, height: 3, background: "var(--ink-150)", borderRadius: 999, overflow: "hidden", display: "inline-block" }}>
            <span style={{ display: "block", height: "100%", width: pct + "%", background: dotColor, borderRadius: 999 }} />
          </span>
          <span className="mono" style={{ fontSize: 11.5, color: "var(--ink-600)" }}>{counts.done}/{counts.total}</span>
        </span>
      );
    }
  }

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "3px 10px", borderRadius: 999,
        background: "white",
        border: "1px solid var(--border)",
        fontSize: 12, fontWeight: 590,
        color: "var(--ink-800)",
        whiteSpace: "nowrap",
      }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
        {label}
      </span>
      {detail}
    </div>
  );
}

export { Departments, Levels, Positions, KPIMini, ValuationStatus };